import { NavLink, Outlet } from 'react-router-dom'
import {
  LayoutDashboard, Users, Building2, CreditCard, Receipt, Mail,
  ScrollText, ShieldCheck, Settings, Send, Twitter, Bot, Radio,
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'

// Admin Panel Redesign — replaces the old flat 9-tab AdminPanel.tsx with a
// sidebar-navigated section layout. Each item below is its own page under
// pages/admin/*, independently fetching its own data (fixes the old
// "one giant loadData() keyed off activeTab" pattern, including the real bug
// where queryLogs state was shared/overwritten between the Users tab's
// "View Logs" action and the Logs tab).
const SECTIONS = [
  { to: 'overview', label: 'Overview', icon: LayoutDashboard },
  { to: 'users', label: 'Users', icon: Users },
  { to: 'organizations', label: 'Organizations', icon: Building2 },
  { to: 'credits', label: 'Credits & Usage', icon: CreditCard },
  { to: 'subscriptions', label: 'Subscriptions & Billing', icon: Receipt },
  { to: 'emails', label: 'Emails', icon: Mail },
  { to: 'logs', label: 'Logs & Audit', icon: ScrollText },
  { to: 'aml', label: 'AML/Compliance', icon: ShieldCheck },
  { to: 'system', label: 'System & Rules', icon: Settings },
  { to: 'streams', label: 'Monitoring Streams', icon: Radio },
  { to: 'telegram', label: 'Telegram', icon: Send },
  { to: 'twitter', label: 'Twitter/X', icon: Twitter },
  { to: 'assistant', label: 'Assistant', icon: Bot },
]

export default function AdminLayout() {
  const { user } = useAuth()

  if (!user || user.role !== 'admin') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="text-center text-destructive">Access Denied</CardTitle>
          </CardHeader>
          <CardContent className="text-center text-muted-foreground">
            You don't have permission to access the admin panel.
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6 lg:flex-row">
      <nav className="shrink-0 lg:w-56">
        <div className="flex gap-1 overflow-x-auto lg:flex-col lg:overflow-visible">
          {SECTIONS.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              {label}
            </NavLink>
          ))}
        </div>
      </nav>
      <div className="min-w-0 flex-1">
        <Outlet />
      </div>
    </div>
  )
}
